import { Badge, ConfigProvider, Popover } from "antd";
import { BellFilled, CheckOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Colors from "infrastructure/utils/colors";
import { NOTIFICATIONS_MAX_BELL_ITEMS } from "../constants";
import { useNotifications } from "../context/NotificationsContext";
import { formatNotificationPresentation } from "../utils/formatNotificationPresentation";
import {
  BellTrigger,
  DisplayAllButton,
  DottedDivider,
  EmptyText,
  MarkAllReadButton,
  Panel,
  PanelFooter,
  PanelHeader,
  PanelTitle,
  PopoverCard,
  PopoverCardBody,
  PopoverCardDescription,
  PopoverCardIcon,
  PopoverCardTitle,
  ScrollBody,
  SectionLabel,
  TitleBlock,
  UnreadSummary,
} from "./NotificationsPopover.style";

type NotificationsPopoverProps = {
  onDisplayAll: () => void;
};

const NotificationsPopover = ({ onDisplayAll }: NotificationsPopoverProps) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir() === "rtl" ? "rtl" : "ltr";
  const isRtl = direction === "rtl";

  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, refreshNotifications, markAllAsRead } =
    useNotifications();

  const visibleItems = useMemo(
    () => notifications.slice(0, NOTIFICATIONS_MAX_BELL_ITEMS),
    [notifications]
  );

  const onOpenChange = async (open: boolean) => {
    setIsOpen(open);
    if (open) {
      await refreshNotifications();
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount > 0) {
      await markAllAsRead();
    }
  };

  const handleDisplayAll = () => {
    setIsOpen(false);
    onDisplayAll();
  };

  const content = (
    <ConfigProvider direction={direction}>
      <Panel $rtl={isRtl}>
        <PanelHeader>
          <TitleBlock>
            <PanelTitle>{t("notifications_title")}</PanelTitle>
            <UnreadSummary>
              {t("notifications_you_have_unread", { count: unreadCount })}
            </UnreadSummary>
          </TitleBlock>
          <MarkAllReadButton
            type="button"
            onClick={() => void handleMarkAllAsRead()}
            disabled={unreadCount === 0}
          >
            <span aria-hidden style={{ display: "inline-flex", letterSpacing: -3 }}>
              <CheckOutlined style={{ fontSize: 11 }} />
              <CheckOutlined style={{ fontSize: 11 }} />
            </span>
            {t("notifications_mark_all_as_read")}
          </MarkAllReadButton>
        </PanelHeader>

        <DottedDivider />

        <ScrollBody>
          <SectionLabel>{t("notifications_earlier")}</SectionLabel>
          {visibleItems.length === 0 ? (
            <EmptyText>{t("notifications_empty")}</EmptyText>
          ) : (
            visibleItems.map((item) => {
              const presentation = formatNotificationPresentation(item, t);
              return (
                <PopoverCard key={item.id}>
                  <PopoverCardIcon>
                    <BellFilled />
                  </PopoverCardIcon>
                  <PopoverCardBody $rtl={isRtl}>
                    <PopoverCardTitle>{presentation.title}</PopoverCardTitle>
                    <PopoverCardDescription>{presentation.body}</PopoverCardDescription>
                  </PopoverCardBody>
                </PopoverCard>
              );
            })
          )}
        </ScrollBody>

        <PanelFooter>
          <DisplayAllButton type="button" onClick={handleDisplayAll}>
            {t("display_all")}
          </DisplayAllButton>
        </PanelFooter>
      </Panel>
    </ConfigProvider>
  );

  return (
    <Popover
      trigger="click"
      open={isOpen}
      onOpenChange={onOpenChange}
      content={content}
      placement={isRtl ? "bottomRight" : "bottomLeft"}
      overlayInnerStyle={{
        padding: 12,
        borderRadius: 16,
        boxShadow: `0 12px 40px ${Colors.shadow}`,
      }}
    >
      <Badge count={unreadCount} size="small" offset={isRtl ? [-2, 2] : [2, 2]}>
        <BellTrigger role="presentation">
          <BellFilled />
        </BellTrigger>
      </Badge>
    </Popover>
  );
};

export default NotificationsPopover;
